import { isPlatformBrowser } from '@angular/common';
import { Component, computed, HostListener, inject, OnInit, PLATFORM_ID, signal, ViewEncapsulation } from '@angular/core';
import { NavigationEnd, Router, RouterLink, RouterLinkActive } from '@angular/router';
import { filter } from 'rxjs/operators';
import { AuthService } from '../../core/services/auth.service';
import { AllProductsService } from '../../features/all-products/all-products.service';
import type { StorefrontCatalogListItem } from '../../features/all-products/storefront-catalog-list.model';
import { CartService } from '../../features/cart/cart.service';
import { WishlistService } from '../../features/wishlist/wishlist.service';
import type { ShopMenuCategoryItem } from './shop-menu.model';
import { ShopMenuService } from './shop-menu.service';

const MEGAMENU_CATALOGS_PER_ROW = 5;
const MEGAMENU_FEATURED_FETCH_SIZE = 50;

type HeaderDropdown = 'account';

@Component({
  selector: 'app-header',
  standalone: true,
  imports: [RouterLink, RouterLinkActive],
  templateUrl: './header.component.html',
  styleUrl: './header.component.scss',
  encapsulation: ViewEncapsulation.None,
})
export class HeaderComponent implements OnInit {
  private readonly platformId = inject(PLATFORM_ID);
  private readonly router = inject(Router);
  private readonly shopMenuService = inject(ShopMenuService);
  private readonly allProductsService = inject(AllProductsService);
  readonly cartService = inject(CartService);
  readonly authService = inject(AuthService);
  private readonly wishlistService = inject(WishlistService);

  readonly searchQuery = signal('');
  readonly mobileNavOpen = signal(false);
  readonly openDropdown = signal<HeaderDropdown | null>(null);
  readonly menuLoading = signal(true);
  readonly menuCategories = signal<ShopMenuCategoryItem[]>([]);
  readonly featuredCatalogs = signal<StorefrontCatalogListItem[]>([]);
  readonly currentPath = signal(this.normalizedPath(this.router.url));

  readonly shopActive = computed(() => {
    const path = this.currentPath();
    return path === '/all-products' || path.startsWith('/all-products/') || path.startsWith('/products/');
  });

  readonly displayName = computed(() => {
    const user = this.authService.user();
    if (!user) {
      return 'My Account';
    }
    const fullName = `${user.firstName ?? ''} ${user.lastName ?? ''}`.trim();
    return fullName || user.email || 'My Account';
  });

  ngOnInit(): void {
    if (isPlatformBrowser(this.platformId)) {
      this.syncSearchQueryFromUrl();
      this.router.events.pipe(filter((e) => e instanceof NavigationEnd)).subscribe(() => {
        this.currentPath.set(this.normalizedPath(this.router.url));
        this.syncSearchQueryFromUrl();
        this.closeMenus();
      });
    } else {
      this.menuLoading.set(false);
      return;
    }

    this.shopMenuService.getShopMenu().subscribe({
      next: (menu) => {
        this.menuCategories.set(menu.categories ?? []);
        this.menuLoading.set(false);
      },
      error: () => {
        this.menuCategories.set([]);
        this.menuLoading.set(false);
      },
    });

    this.allProductsService
      .getCatalogList({
        pageNumber: 1,
        pageSize: MEGAMENU_FEATURED_FETCH_SIZE,
        sortBy: 'manual',
        categoryIds: [],
        productTypes: [],
      })
      .subscribe({
        next: (res) => {
          this.featuredCatalogs.set(this.pickFeaturedCatalogs(res.products.items));
        },
        error: () => {
          this.featuredCatalogs.set([]);
        },
      });
  }

  megamenuCatalogs(category: ShopMenuCategoryItem): ShopMenuCategoryItem['catalogs'] {
    return category.catalogs.slice(0, MEGAMENU_CATALOGS_PER_ROW);
  }

  featuredCatalogSlug(product: StorefrontCatalogListItem): string {
    const variant = product.variants.find((v) => v.isDefault) ?? product.variants[0];
    return variant?.slug ?? product.slug;
  }

  toggleDropdown(event: Event, name: HeaderDropdown): void {
    event.preventDefault();
    event.stopPropagation();
    this.openDropdown.update((current) => (current === name ? null : name));
  }

  toggleMobileNav(): void {
    this.mobileNavOpen.update((open) => !open);
    if (!this.mobileNavOpen()) {
      this.openDropdown.set(null);
    }
  }

  closeMenus(): void {
    this.openDropdown.set(null);
    this.mobileNavOpen.set(false);
  }

  onBagClick(event: Event): void {
    event.preventDefault();
    this.closeMenus();
    this.cartService.openDrawer();
  }

  onSearch(event: Event): void {
    event.preventDefault();
    const q = this.searchQuery().trim();
    this.closeMenus();
    void this.router.navigate(['/all-products'], q ? { queryParams: { q } } : {});
  }

  onSignOut(event: Event): void {
    event.preventDefault();
    this.closeMenus();
    this.authService.logout();
    this.wishlistService.clearOnLogout();
    void this.router.navigate(['/login']);
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    const target = event.target as Element | null;
    if (!target?.closest('.dropdown, .header__account-holder')) {
      this.openDropdown.set(null);
    }
  }

  private normalizedPath(url: string): string {
    return url.split('?')[0].split('#')[0];
  }

  private syncSearchQueryFromUrl(): void {
    if (this.normalizedPath(this.router.url) !== '/all-products') {
      return;
    }
    const q = this.router.parseUrl(this.router.url).queryParams['q'];
    this.searchQuery.set(typeof q === 'string' ? q : '');
  }

  private pickFeaturedCatalogs(items: StorefrontCatalogListItem[]): StorefrontCatalogListItem[] {
    const promoted = items.filter((product) => this.hasActivePromotion(product));
    const pool = promoted.length > 0 ? promoted : items;
    return this.shuffle(pool).slice(0, MEGAMENU_CATALOGS_PER_ROW);
  }

  private hasActivePromotion(product: StorefrontCatalogListItem): boolean {
    const variant = product.variants.find((v) => v.isDefault) ?? product.variants[0];
    const promo = variant?.promotionalPrice ?? product.promotionalPrice;
    const list = variant?.salePrice ?? product.listPrice;
    return promo != null && promo < list;
  }

  private shuffle<T>(items: T[]): T[] {
    const copy = [...items];
    for (let i = copy.length - 1; i > 0; i -= 1) {
      const j = Math.floor(Math.random() * (i + 1));
      [copy[i], copy[j]] = [copy[j], copy[i]];
    }
    return copy;
  }
}
